export default {
  failed: 'Action failed',
  success: 'Action was successful',

  // Common
  common: {
    back: 'Back',
    continue: 'Continue',
    next: 'Next',
    cancel: 'Cancel',
    dismiss: 'Dismiss',
    retry: 'Retry',
    close: 'Close',
    required: 'Required',
    loading: 'Loading...',
    min: 'min',
    today: 'Today',
    noSlots: 'No slots',
    noAppointments: 'No appointments',
    noAppointmentsDesc: 'No appointments match the criteria.',
    noResults: 'No results found',
    success: 'Saved successfully',
    failed: 'Saving failed',
    actions: 'Actions',
    show: 'Show',
    hide: 'Hide',
  },

  // Admin Layout
  admin: {
    title: 'Appointment Admin',
    navigation: 'Navigation',
    permissionsPage: {
      empty: 'No permissions have been assigned yet.',
      removeUser: 'Remove user',
      addTitle: 'Add permissions for a new user',
      addUser: 'Add user',
      loadFailed: 'Failed to load permissions',
      updated: 'Permissions updated',
      updateFailed: 'Failed to update permissions',
      confirmRemove: 'Are you sure you want to remove all permissions for {email}?',
      alreadyExists: 'User already exists in the list',
    },
    permissionsSubtitle: 'Manage fine-grained access for specific users',
    nav: {
      booking: 'Booking',
      administration: 'Administration',
      permissions: 'Permissions',
      site: 'Site',
      darkMode: 'Dark mode',
      lightMode: 'Light mode',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
    },
    calendar: {
      title: 'Calendar',
    },
    invoices: {
      title: 'Invoices',
      caption: 'Invoice actions',
    },
    patients: {
      title: 'Patients',
    },
    doctolibConfig: {
      title: 'Doctolib Config',
      caption: 'Manage connection',
    },
    apolloConfig: {
      title: 'Apollo Config',
      caption: 'Manage connection',
    },
    integrations: {
      title: 'Integrations',
      testConnection: 'Test Connection',
      testingConnection: 'Testing connection...',
      connectionSuccess: 'Connection successful',
      connectionFailed: 'Connection failed',
      refreshAccountInfo: 'Refresh Account Info',
      logout: 'Logout',
      loadingStatus: 'Checking authentication status…',
      connectionDisconnectedTitle: 'Provider Connection Disconnected',
      connectionDisconnectedAdminMessage:
        'Accessing this area requires an active connection to the provider interfaces (Doctolib and Apollo). Please verify the connection in the Integrations configuration.',
      connectionDisconnectedUserMessage:
        'Accessing this area requires an active connection to the booking system. The connection is currently disconnected. Please contact your system administrator to restore access.',
      connectionDisconnectedLexwareAdminMessage:
        'Accessing this area requires an active connection to the interfaces (Lexware and Apollo). Please verify the connection in the Integrations configuration.',
      connectionDisconnectedLexwareUserMessage:
        'Accessing this area requires an active connection to the invoicing system. The connection is currently disconnected. Please contact your system administrator to restore access.',
      goToIntegrations: 'Go to Integrations',
    },
    configEditor: {
      title: 'Configuration',
      search: 'Search settings...',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Configuration saved successfully',
      reset: 'Reset changes',
      error: 'Failed to save configuration',
      exportYaml: 'Export YAML',
      importYaml: 'Import YAML',
      importSuccess: 'YAML imported successfully',
      importError: 'Failed to import YAML',
    },
    mappings: {
      title: 'Mappings',
      caption: 'Manage cross-provider mappings',
      subtitle:
        "Maps calendars, examinations and safety questions of this site's booking channel (Doctolib, form or Principa) to Apollo options.",
      subtitleDetail: '{source} → {target}',
      loading: 'Loading mapping data…',
      noTypes: 'No mapping types available.',
      addRow: 'Add mapping',
      removeRow: 'Remove mapping',
      save: 'Save mappings',
      refreshOptions: 'Refresh options',
      removedInvalid: 'Removed {count} invalid mapping entries.',
      refreshSuccess: 'Mapping options refreshed',
      refreshFailed: 'Failed to refresh mapping options',
      saveSuccess: 'Mappings saved',
      saveFailed: 'Failed to save mappings',
      duplicateSources: 'Each source can appear only once. Duplicates: {list}',
      duplicateTargets: 'Each target can appear only once. Duplicates: {list}',
      duplicateSourcesTargets:
        'Sources and targets must be unique. Sources: {sources}. Targets: {targets}.',
      invalidRelation: 'Relation violated in rows: {rows}',
      unmappedSourcesSummary: '{count} source options are not mapped yet.',
      allSourcesMapped: 'All source options are mapped.',
      showOnlyUnmappedSources: 'Show only unmapped sources',
      showUnmappedList: 'Show list',
      hideUnmappedList: 'Hide list',
      unmappedSourcesListTitle: 'Unmapped sources',
      noUnmappedSources: 'No unmapped sources available.',
      relation: {
        one_to_one: 'Relation: one-to-one',
        one_to_many: 'Relation: one-to-many',
        many_to_one: 'Relation: many-to-one',
        many_to_many: 'Relation: many-to-many',
      },
    },
    unauthorized: {
      title: 'No Permissions',
      message:
        'Your account ({email}) currently has no assigned permissions for this area. Please contact an administrator to gain access to the calendar, bookings, or invoices.',
      restrictedSite:
        'You do not have access to the currently selected location "{siteName}". Please select another location at the top that your account is authorized for.',
      permittedSitesTitle: 'Available Locations for Your Account',
      logout: 'Logout',
    },
  },

  // Admin OAuth
  adminAuth: {
    sessionExpired: 'Session expired',
    sessionRemaining: 'Session ends in',
    extendSession: 'Extend',
    title: 'Administrator Access',
    subtitle: 'Sign in with your Apollo account to open the admin area.',
    login: 'Sign in with Apollo',
    logout: 'Sign out',
    continue: 'Go to Admin Dashboard',
    authenticated: 'Apollo session detected',
    signedInAs: 'Signed in as',
    unknownUser: 'Unknown user',
    unknownEmail: 'Unknown email',
    roles: 'Roles',
    noRoles: 'No roles in token',
    missingRole: 'Your account is missing the required administrator role.',
    misconfigured: 'OAuth configuration missing. Please contact support.',
    menuLabel: 'Administrator menu',
    menuButton: 'Admin',
  },

  // Apollo Config
  apollo: {
    title: 'Apollo Configuration',
    subtitle: 'Configure Apollo credentials for appointment booking',
    authStatus: 'Authentication Status',
    authenticated: 'Authenticated',
    notAuthenticated: 'Not Authenticated',
    logout: 'Logout',
    loginRequired: 'Login required',
    refreshAccountInfo: 'Refresh Account Info',
    accountInfo: 'Account Information',
    accountInfoRefreshed: 'Account info refreshed',
    failedToRefresh: 'Failed to refresh account info',

    credentials: 'Apollo Credentials',
    email: 'Email Address',
    emailRequired: 'Email is required',
    password: 'Password',
    passwordRequired: 'Password is required',
    authenticate: 'Authenticate',

    twoFactorAuth: 'Two-Factor Authentication',
    twoFactorMessage:
      'A verification code has been sent to your email. Please check your inbox and enter the code below.',
    enterCode: 'Enter verification code',
    codeRequired: 'Code is required',
    verifyCode: 'Verify Code',
    resendCode: 'Resend Code',
    codeResent: '2FA code resent - Check your email for the new code',
    failedToResend: 'Failed to resend code',

    authSuccess: 'Authentication successful!',
    authFailed: 'Authentication failed',
    twoFactorSuccess: '2FA verification successful!',
    twoFactorFailed: '2FA verification failed',
    invalidSession: 'Invalid session',
    logoutSuccess: 'Logged out and credentials cleared',
    logoutFailed: 'Logout failed',

    apolloOptionsRefreshed: 'Apollo cached options refreshed successfully',

    startCalendarSync: 'Start Calendar Sync',
    syncStarting: 'Starting calendar sync...',
    syncStartingCaption: 'This may take a few moments',
    syncCompleteSuccess: 'Calendar sync complete - No inconsistencies found',
    syncCompleteSuccessCaption: 'Synced: {count}',
    syncCompleteDiscrepancies: 'Sync complete',
    syncCompleteDiscrepanciesCaption:
      'Conflicts: {conflicts}, Doctolib only: {doctolib}, Apollo only: {apollo}',
    syncFailed: 'Calendar sync failed',
    viewDetails: 'View Details',
    syncReportTitle: 'Sync Report',
    syncReportMessage:
      'Found {count} inconsistencies (Synced: {synced}, Conflicts: {conflicts}, Doctolib only: {doctolib}, Apollo only: {apollo}). Check console for details.',
  },

  invoices: {
    title: 'Invoices',
    loadReady: 'Load finished examinations',
    daysBack: 'Days back',
    syncButton: 'Create Lexware draft',
    openLexwareDrafts: 'Open Lexware Drafts',
    drafts: 'Drafts',
    halfDoneTitle: 'Examination {id}: Lexware draft created, Apollo NOT updated',
    halfDoneSendTitle: 'Examination {id}: Sent to LetterXpress, Apollo NOT updated',
    validationTitle: 'Examination {id} cannot be invoiced',
    openInLexware: 'Open in Lexware',
    openApolloInvoice: 'Open invoice in Apollo',
    fetchAllHistorical: 'Fetch all historical',
    loadFailed: 'Failed to load invoice-ready examinations',
    loadFailedStale: 'The list below is from the last successful load and may be out of date.',
    syncSuccess: 'Lexware invoice created',
    syncFailed: 'Lexware sync failed',
    filterPatient: 'Patient',
    filterStatus: 'Status',
    filterStatusAll: 'All',
    filterStatusReady: 'No Invoice',
    filterStatusSentOut: 'Transferred',
    filterStatusProcessed: 'Processed',
    filterStatusNeedsChange: 'Needs Review',
    stageLabel: 'Stage',
    stageApollo: 'Apollo',
    stageLexware: 'Lexware',
    stageLetterxpress: 'LetterXpress',
    stageProcessed: 'Processed',
    stageNoDispatch: 'No Dispatch',
    markProcessedButton: 'Invoice Finished',
    apolloStatusLabel: 'Apollo status',
    statusColumnLabel: 'Status',
    unlinkLexwareButton: 'Unlink Lexware',
    unlinkNoDispatchButton: 'Undo No Dispatch',
    confirmUnlinkLexware:
      'This only clears the link in our system (the Lexware id is removed from Apollo). Nothing is deleted in Lexware itself - do that there by hand if needed. Continue?',
    confirmUnlinkNoDispatch:
      'This will clear the "No Dispatch" mark. The invoice will be displayed in "Transferred" again. Continue?',
    unlinkedLexware: 'Lexware link cleared',
    unlinkedNoDispatch: 'Cleared "No Dispatch" mark',
    filterDateFrom: 'Date from',
    filterDateTo: 'Date to',
    filterReset: 'Reset filters',
    resultsCount: '{count} of {total} shown',
    examinationDate: 'Date',
    patientLabel: 'Patient',
    amountLabel: 'Amount',
    lexwareDocument: 'Lexware Document',
    insuranceLabel: 'Insurance',
    referrerLabel: 'Referrer',
    firstExaminerLabel: 'First Examiner',
    deviceLabel: 'Device',
    paymentOptionLabel: 'Payment Option',
    recipientLabel: 'Invoice Recipient',
    missingAddressWarning: 'No address set in Apollo',
    selectAll: 'Select all',
    selectRow: 'Select examination {id}',
    selectedSummary: '{count} selected · {amount}',
    clearSelection: 'Clear selection',
    bulkRunning: {
      sync: 'Creating Lexware drafts',
      send: 'Sending letters to LetterXpress',
      markProcessed: 'Completing invoices',
    },
    bulkCancel: 'Stop',
    bulkCancelling: 'Stopping after the current invoice …',
    bulkConfirmSend:
      'Send {count} invoices (total {amount}) via LetterXpress? The letters will be printed and posted.',
    bulkConfirmMarkProcessed:
      'Mark {count} invoices (total {amount}) as completed without sending?',
    bulkSucceeded: 'Bulk action finished: {count} done',
    bulkResultTitle: 'Bulk action: {succeeded} of {total} done',
    bulkStoppedError:
      'Stopped after an error so nothing is created twice. {count} more were not processed.',
    bulkStoppedCancel: 'Stopped. {count} more were not processed.',
  },

  // User Layout
  user: {
    bookingUnavailable: 'Booking Unavailable',
    bookingUnavailableMessage: 'Booking is currently not possible. Please try again later.',
  },

  // Calendar View
  calendar: {
    searchPlaceholder: 'Search patient or examination...',
    calendars: 'Calendars',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    today: 'Today',
    pickDate: 'Pick a date',
    previous: 'Previous',
    next: 'Next',
    weekdays: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
    },
    loadingAppointments: 'Loading appointments...',
    failedToLoad: 'Failed to load appointments',
    unknown: 'Unknown',
    unknownExam: 'Unknown Exam',
    more: 'more',
    backwardsLimitReached: 'Calendar limited to 6 months back!',
  },

  // Doctolib Config
  doctolib: {
    title: 'Doctolib Configuration',
    subtitle: 'Configure Doctolib credentials for appointment booking',
    authStatus: 'Authentication Status',
    authenticated: 'Authenticated',
    notAuthenticated: 'Not Authenticated',
    logout: 'Logout',
    loginRequired: 'Login required',
    refreshAccountInfo: 'Refresh Account Info',
    accountInfo: 'Account Information',
    accountInfoRefreshed: 'Account info refreshed',
    failedToRefresh: 'Failed to refresh account info',

    credentials: 'Doctolib Credentials',
    email: 'Email Address',
    emailRequired: 'Email is required',
    password: 'Password Hash',
    passwordRequired: 'Password is required',
    passwordHint: 'Your Doctolib password (will be hashed automatically)',
    pin: 'PIN',
    pinHint: '4-digit PIN if enabled for your account',
    authenticate: 'Authenticate',

    pinRequired: 'PIN Required',
    enterPin: 'Enter 4-digit PIN',
    pinMustBe4Digits: 'PIN must be 4 digits',
    submitPin: 'Submit PIN',

    twoFactorAuth: 'Two-Factor Authentication',
    twoFactorMessage:
      'A verification code has been sent to your email. Please check your inbox and enter the code below.',
    enterCode: 'Enter verification code',
    codeRequired: 'Code is required',
    verifyCode: 'Verify Code',
    resendCode: 'Resend Code',
    codeResent: '2FA code resent - Check your email for the new code',
    failedToResend: 'Failed to resend code',

    authSuccess: 'Authentication successful!',
    authFailed: 'Authentication failed',
    pinVerificationSuccess: 'PIN verification successful!',
    pinVerificationFailed: 'PIN verification failed',
    twoFactorSuccess: '2FA verification successful!',
    twoFactorFailed: '2FA verification failed',
    invalidSession: 'Invalid session',
    logoutSuccess: 'Logged out and credentials cleared',
    logoutInconsistent: 'Logout action completed but status might be inconsistent',
    logoutFailed: 'Logout failed',
  },

  serviceDesk: {
    title: 'Service Desk',
    location: 'Location',
    calendar: 'Calendar',
    allCalendars: 'All calendars',
    stepExamination: 'Examination',
    stepAppointment: 'Appointment',
    stepPatient: 'Patient',
    insurance: 'Insurance',
    change: 'Change',
    chooseInsuranceFirst: 'Choose the insurance type first',
    chooseExaminationFirst: 'Choose an examination first',
    patientTitle: 'Title',
    gender: 'Gender',
    firstName: 'First name',
    lastName: 'Last name',
    birthdate: 'Date of birth',
    phone: 'Phone',
    email: 'Email',
    additionalQuestions: 'Additional questions',
    summary: 'Booking',
    patient: 'Patient',
    noSlot: 'No appointment selected',
    acquisition: 'Acquisition channel',
    emailLanguage: 'Email language',
    bookingCodeEmail: 'Email for booking code',
    bookingCodeEmailHint:
      'Principa sends the booking code to your own address, never to the patient.',
    booking: 'Booking...',
    bookingCodeTitle: 'Enter booking code',
    bookingCodePlaceholder: 'Booking code',
    confirm: 'Confirm',
  },

  patients: {
    patient: 'Patient',
    openOverview: 'Open patient overview',
    searchPlaceholder: 'Name, phone number, email, birth date (DD.MM.YYYY) or patient ID',
    searchHint: 'Enter a search term',
    incomingCall: 'Incoming call',
    hits: 'Results',
    noHits: 'No patients found',
    refineSearch: 'There may be more results - refine the search.',
    searchError: 'Patient search failed',
    loadError: 'Patient could not be loaded',
    selectPatient: 'Select a patient from the results',
    matchedOn: {
      phone: 'Phone',
      name: 'Name',
      email: 'Email',
      birth_date: 'Birth date',
      id: 'Patient ID',
    },
    openInApollo: 'Open in Apollo',
    newAppointment: 'New appointment',
    phone: 'Phone',
    mobile: 'Mobile',
    email: 'Email',
    address: 'Address',
    remarks: 'Remarks',
    examinations: 'Examinations',
    noExaminations: 'No examinations',
    allOffices: 'All locations',
    date: 'Date',
    office: 'Location',
    examination: 'Examination',
    status: 'Status',
    invoice: 'Invoice',
    dispatched: 'Letter sent',
    noDispatch: 'No letter',
  },

  principa: {
    title: 'Principa',
    subtitle: 'Appointment booking (Siegele Software / PlanOrg)',
    authenticated: 'Connected',
    notAuthenticated: 'Not connected',
    statusRefreshed: 'Status refreshed',
    refreshInProgress:
      'Refreshing - this can take a few minutes since every examination type is checked with Principa individually.',
  },

  lexware: {
    title: 'Lexware',
    subtitle: 'ERP & Accounting',
    authenticated: 'Authenticated',
    notAuthenticated: 'Not Authenticated',
    refreshStatus: 'Refresh Account Info',
    statusRefreshed: 'Status refreshed',
    failedToRefresh: 'Failed to refresh status',
    organization: 'Organization',
    user: 'User',
    features: 'Features',
    connectionId: 'Connection ID',
  },

  lexwareStatus: {
    label: 'Lexware status',
    loading: 'Loading Lexware status',
    unknown: 'unknown',
    unknownHint: 'The status could not be read from Lexware',
    openOf: 'open: {open} of {total}',
    paidOn: 'paid on {date}',
    dueOn: 'due on {date}',
    status: {
      draft: 'Draft',
      open: 'Open',
      overdue: 'Overdue',
      paid: 'Paid',
      voided: 'Voided',
      transferred: 'Transfer pending',
      sepadebit: 'Direct debit',
    },
  },

  letterxpress: {
    title: 'LetterXpress',
    subtitle: 'Postal dispatch',
    authenticated: 'Authenticated',
    notAuthenticated: 'Not Authenticated',
    statusRefreshed: 'Status refreshed',
    balance: 'Balance',
    sendButton: 'Send to LetterXpress',
    sendSuccess: 'Sent to LetterXpress.',
    unlinkButton: 'Unlink LetterXpress',
    confirmUnlink:
      'This only clears the link in our system (the LetterXpress job id is removed from Apollo). Nothing is deleted at LetterXpress itself. Continue?',
    unlinked: 'LetterXpress link cleared',
    addressMismatchToast:
      'The address LetterXpress detected differs from the expected one - please check.',
    addressMismatchTitle: 'Examination {id}: LetterXpress address differs',
    addressMismatchExpected: 'Expected',
    addressMismatchDetected: 'Detected by LetterXpress',
  },

  // Booking Page
  booking: {
    privacyConsentText1:
      'I agree that this website stores my submitted information so that my request can be answered. The data will be processed in accordance with the ',
    privacyConsentLink: 'privacy policy',
    privacyConsentText2: '. *',
    insuranceBG: 'Berufsgenossenschaft (BG)',
    insurancePrivate: 'Privately insured',
    insuranceSelfPayer: 'Self-payer',
    noSlotsForSelection: 'No online appointments are currently available for your selection.',
    loadDemoData: 'Load demo data:',
    salutation: 'Salutation',
    emailAddress: 'Email Address',
    streetAndNumber: 'Street & House Number',
    cityLabel: 'City',
    submitting: 'Submitting...',

    title: 'Book Appointment',
    insurance: 'Insurance',
    bookDirect: 'Book Direct',
    clear: 'Clear',
    openInCalendar: 'Open in Calendar',

    // Step 1: Location
    step1Title: 'Location',
    chooseLocation: 'Choose Location',
    selectLocation: 'Please select a location',

    // Step 2: Insurance
    step2Title: 'Insurance',
    chooseInsurance: 'Choose Insurance',
    BG: 'BG',
    Private: 'Private',
    'Self-payer': 'Self-payer',
    insurance_bg: 'BG',
    insurance_private: 'Private',
    insurance_self_payer: 'Self-payer',
    selectInsurance: 'Please select your insurance type',

    // Step 3: Appointment Type
    step3Title: 'Appointment Type',
    chooseCategory: 'Choose Appointment Type',
    selectCategory: 'Please select an appointment type',

    // Step 4: Reason for Visit
    step4Title: 'Reason for Visit',
    chooseExamination: 'Choose Reason for Visit',
    searchExaminations: 'Search reasons for visit',
    other: 'Other',
    selectAtLeastOne: 'Please select one reason for visit',

    // Step 5: Appointment
    step5Title: 'Appointment',
    selectAppointmentTime: 'Select Appointment Time',
    calendarOptional: 'Calendar (Optional)',
    selectTimeSlot: 'Please select a time slot',
    failedToLoadSlots: 'Failed to load slots',
    noSlotsAvailable: 'No slots available for the current selection.',
    selectedSlot: 'Selected slot',
    preferredTimes: 'Preferred Times',
    selectPreferredTimes: 'Please select at least one preferred time.',
    selectPreferredTimesDesc: 'Please select your preferred times.',
    selected: 'Selected',
    preferredTime: 'Preferred Time',
    preferredTimesPlural: 'Preferred Times',

    // Step 6: Your Details
    step6Title: 'Your Details',
    personalInformation: 'Personal Information',
    fillDemoData: 'Fill Demo Data',
    patientTitle: 'Title',
    firstName: 'First Name',
    lastName: 'Last Name',
    gender: 'Gender',
    dateOfBirth: 'Date of Birth',
    contact: 'Contact',
    email: 'Email',
    invalidEmail: 'Invalid email',
    phoneNumber: 'Phone Number',
    address: 'Address',
    street: 'Street',
    zipCode: 'Zip Code',
    city: 'City',
    country: 'Country',
    medicalInformation: 'Medical Information',
    allergies: 'Allergies',
    cardiacPacemaker: 'Cardiac Pacemaker',
    claustrophobia: 'Claustrophobia',
    metalImplant: 'Metal Implant',
    bedridden: 'Bedridden',
    externalRisk: 'External Risk',
    kidneyProblem: 'Kidney Problem known',
    neurostimulator: 'Neurostimulator',
    selfHarm: 'Self Harm',
    accompanied: 'Patient must be accompanied',
    additionalQuestions: 'Additional Questions:',
    requiredFields: '* Required fields',
    fillRequiredFields: 'Please fill in required fields',
    fillRequiredReason: 'Please provide a reason for the visit',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderDiverse: 'Diverse',
    genderIndeterminate: 'Indeterminate',
    genderNone: 'Prefer not to say',

    // Step 7: Summary
    step7Title: 'Summary',
    confirmBooking: 'Confirm Booking Request',
    practice: 'Practice',
    category: 'Appointment Type',
    examination: 'Reason for Visit',
    dateTime: 'Date & Time',
    patient: 'Patient',
    confirmAndBook: 'Confirm & Book',
    bookingYourAppointment: 'Requesting your appointment...',

    dynamicFields: {
      examination_reason: {
        question: 'Reason for the visit',
        shortLabel: 'Brief description of your symptoms',
      },
    },
  },

  // Form booking (magazin/network, embedded on the websites)
  bookingForm: {
    heading: 'Request an appointment',
    lead: 'A few steps to your examination. It takes about 2 minutes.',
    stepOf: 'Step {current} of {total}',
    loadFailed:
      'The examinations could not be loaded right now. Please try again in a few minutes.',
    reload: 'Reload page',
    back: 'Back',
    backToStep: 'Back to step {step}: {label}',
    next: 'Continue',
    toReview: 'Continue to summary',
    steps: {
      insurance: 'Insurance',
      examination: 'Examination',
      time: 'Appointment',
      contact: 'Contact details',
      review: 'Review and send',
    },
    insuranceTitle: 'How are you insured?',
    insuranceDescBG: 'After an accident at work or on the way to work',
    insuranceDescPrivate: 'Private health insurance or Beihilfe',
    insuranceDescSelfPayer: 'You pay for the examination yourself',
    examinationTitle: 'Which examination do you need?',
    department: 'Department',
    examination: 'Examination',
    duration: 'approx. {minutes} min.',
    noExaminations: 'Unfortunately, no examination can be requested online for this selection.',
    timeTitle: 'When suits you?',
    preferredHint:
      'Select every time that suits you. We will then get back to you with a fixed appointment.',
    selectAll: "I'm flexible: select all",
    timesSelected: 'No time selected yet | {n} time selected | {n} times selected',
    selectedSlot: 'Your appointment',
    change: 'Change',
    contactTitle: 'How can we reach you?',
    person: 'About you',
    contact: 'Contact',
    address: 'Address',
    medical: 'About the examination',
    optional: '(optional)',
    salutationPlaceholder: 'e.g. Dr',
    day: 'Day',
    month: 'Month',
    year: 'Year',
    dayPlaceholder: 'DD',
    monthPlaceholder: 'MM',
    yearPlaceholder: 'YYYY',
    zipCode: 'Postcode',
    yes: 'Yes',
    no: 'No',
    errorSummary: 'Please check one more entry: | Please check {n} more entries:',
    errors: {
      insurance: 'Please select your insurance.',
      examination: 'Please select an examination.',
      preferredTimes: 'Please select at least one time.',
      slot: 'Please select an appointment.',
      gender: 'Please select your gender.',
      firstName: 'Please enter your first name.',
      lastName: 'Please enter your last name.',
      birthdate: 'Please enter your full date of birth, e.g. 07 03 1952.',
      phone: 'Please enter a phone number.',
      email: 'Please enter your email address.',
      emailInvalid: "Please enter a valid email address, e.g. name{'@'}example.com.",
      street: 'Please enter your street and house number.',
      zipCode: 'Please enter your postcode.',
      city: 'Please enter your city.',
      country: 'Please select your country.',
      answer: 'Please answer this question.',
      required: 'Please fill in this field.',
      privacy: 'Please agree to the privacy notice so that we can process your request.',
    },
    reviewTitle: 'Please check your details',
    reviewPerson: 'Your details',
    bornOn: 'born {date}',
    submit: 'Send appointment request',
    submitNotePreferred:
      'This is not a fixed booking yet. We will get back to you with a proposed appointment.',
    submitNoteVerify:
      'After sending, we will email you. Please confirm your request using the link in that email.',
    weekdays: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
    },
  },

  // Verify Booking
  verify: {
    verifying: 'Verifying your appointment...',
    confirmed: 'Confirmed!',
    appointmentBooked: 'Your appointment is now booked.',
    confirmationEmailSent: 'A confirmation email has been sent to your email address.',
    requestReceived: 'Request Submitted!',
    requestSubmitted: 'Your appointment request was successfully submitted.',
    requestConfirmationEmailSent:
      'We are reviewing your request and will send you a confirmation email shortly.',
    date: 'Date',
    time: 'Time',
    bookAnother: 'Request Another Appointment',

    bookingUnavailable: 'Booking Unavailable',
    verificationFailed: 'Verification Failed',
    invalidOrExpired: 'Invalid or expired link',
    alreadyConfirmedMessage:
      'This could mean the appointment was already confirmed, or the time slot is no longer available.',
    expiredMessage:
      'The verification link may have expired (24 hours) or the appointment slot is no longer available.',
    confirmationEmailNote:
      'If your booking was successful, you should have received a confirmation email.',
    startNewBooking: 'Start New Booking',
    bookingCouldNotBeCompleted: 'Booking could not be completed',
    noTokenProvided: 'No verification token provided',
    slotNoLongerAvailable: 'Verification failed. The slot may no longer be available.',
  },

  // Request Status
  status: {
    checkYourEmail: 'Check your email',
    requestSentSuccess: 'Booking request sent successfully!',
    verificationLinkSent:
      "We've sent a verification link to your email address. Please click the link to confirm your appointment.",
    linkExpiresInfoRequest: 'The link is valid for 24 hours.',
    linkExpiresInfo:
      'The link expires in 24 hours. The appointment slot is reserved for 15 minutes.',

    requestFailed: 'Request Failed',
    somethingWentWrong: 'Something went wrong',
    requestFailedMessage:
      "We couldn't process your booking request at this time. Please try again or contact support.",
    tryAgain: 'Try Again',
  },

  // Error Pages
  error: {
    notFound: 'Page Not Found',
    notFoundTitle: 'Page Not Found',
    notFoundMessage: 'Oops. Nothing here...',
    goHome: 'Go Home',
    offlineTitle: 'Server Offline',
    offlineMessage:
      'We cannot establish a connection to our servers. Please check your internet connection and try again.',
    retry: 'Retry',
  },

  // Sync
  sync: {
    request: 'Request',
    examination: 'Examination',
    tabLabel: 'Conflicts',
    filterByCalendar: 'Filter by Calendar',
    all: 'All',
    none: 'None',
    noCalendarsFound: 'No calendars found',
    runSyncToSeeCalendars: 'Run a sync to see calendars',
    summaryTitle: 'Sync Summary',
    synced: 'Synced',
    conflicts: 'Conflicts',
    missing: 'Missing',
    fieldMismatches: 'Field Mismatches',
    missingInApollo: 'Missing in Apollo',
    onlyInApollo: 'Only in Apollo',
    allInSync: 'All systems in sync',
    runSyncToSeeResults: 'Run sync to see results',
    syncButton: 'Sync',
    startingSync: 'Starting calendar sync...',
    dataTooOld: 'Sync data is older than 10 minutes. Please refresh.',
    refreshNow: 'Refresh Now',
    syncingData: 'Syncing data...',
    pleaseWait: 'Please wait a moment',
    syncCompleteDiscrepancies: 'Sync complete: {count} discrepancies found',
    syncCompleteSuccess: 'Calendar sync complete - All systems in sync',
    syncFailed: 'Calendar sync failed',
    appointmentMatch: 'Appointment Match',
    appointmentDetails: 'Appointment Details',
    notInDoctolib: 'Not in Doctolib',
    notInApollo: 'Not in Apollo',
    notInApolloDesc:
      'This appointment only exists in Doctolib. Please select a source (acquisition channel) to create it in Apollo:',
    sourceAcquisitionChannel: 'Source (Acquisition Channel)',
    sourceAcquisitionChannelPlaceholder: 'Acquisition channel...',
    problemDescription: 'Problem Description:',
    issuesFound: 'Differences:',
    syncDoctolibToApollo: 'Update Apollo with Doctolib data',
    createInApollo: 'Create in Apollo',
    deleteFromApollo: 'Remove from Apollo',
    actionSuccess: 'Action completed successfully',
    resolutionFailed: 'Resolution failed',
    viewInDoctolib: 'View in Doctolib',
    viewInApollo: 'View in Apollo',
    patient: 'Patient',
    date: 'Date',
    timeslot: 'Timeslot',
    status: 'Status',
    insurance: 'Insurance',
    notes: 'Notes',
    remarks: 'Remarks',
    medicalSafety: 'Medical Safety',
    noSafetyChecks: 'No safety information',
    allergies: 'Allergies',
    pacemaker: 'Pacemaker',
    metalImplant: 'Metal Implant',
    claustrophobia: 'Claustrophobia',
    examinationType: 'Examination Type',
    calendar: 'Calendar',
    device: 'Device',
    office: 'Office',
    duration: 'Duration',
    examinationReason: 'Examination Reason',
    appointmentId: 'Appointment ID',
    reason: 'Reason',
    unknownPatient: 'Unknown Patient',
    apolloExamination: 'Apollo Examinations',
    apolloRequest: 'Apollo Requests',
    noAssignedCalendar: 'No Assigned Calendar',
    unknownAppointment: 'Appointment',
    notProvided: 'Not provided',
    fixableViaSync: 'Fixable via Sync',
    manualActionRequired: 'Manual action required',
    manualActionRequiredLong: 'These differences must be resolved manually in Apollo.',
    updateExaminationNotSupported:
      'Updates of examinations are only possible for appointment requests via the UI. Please use Apollo directly.',
    partialFixHint: '* Only patient data, times, and notes will be updated automatically.',
    editInApollo: 'Edit in Apollo',
    daySchedule: 'Day Schedule',
    discrepancies: 'Discrepancies',
    doctolib_only: 'Only in Doctolib',
    apollo_only: 'Only in Apollo',
    conflict: 'Conflict',
    issue: 'Issue',
    issues: 'Issues',
    patient_name: 'Patient Name',
    patient_email: 'Email',
    patient_birth_date: 'Birth Date',
    patient_phone: 'Phone',
    patient_gender: 'Gender',
    patient_insurance_type: 'Insurance',
    patient_street: 'Street',
    patient_zip_code: 'Zip Code',
    patient_city: 'City',
    appointment_status: 'Status',
    start_datetime: 'Appointment Time',
    examination_name: 'Examination',
    examination_reason: 'Reason',
    site_key: 'Location',
    device_name: 'Device',
    external_provider_id: 'Provider ID',
    bookingDirectSuccess: 'Appointment booked: {exam} - {patient} ({date} at {time})',
  },

  // Predikto
  predikto: {
    title: 'Book your appointment',
    bookNow: 'Book Now',
    select: 'Select',
    selected: 'Selected',
    included: 'Included',
    alreadyAdded: 'Already added',
    addToCart: 'Add to cart',
    duration: 'Duration',
    steps: {
      examinations: 'Examinations',
      location: 'Location',
      appointment: 'Appointment',
      personalData: 'Personal Data',
      summary: 'Summary',
    },
    validation: {
      selectGroup: 'Please select a package to continue',
      invalidSelection: 'Please ensure your selection is valid',
      selectLocation: 'Please select a location',
      selectSlot: 'Please select an appointment time',
      fillRequired: 'Please fill all required fields',
    },
    sidebar: {
      title: 'Your Booking',
      examinations: 'Examinations',
      location: 'Location',
      appointment: 'Appointment',
      patient: 'Patient',
      minutes: 'min',
      edit: 'Edit',
    },
    selection: {
      heading: 'Choose your Predikto scan',
      subtitle:
        'Whole-body MRI for comprehensive screening or specialized MRI scans for targeted screening of specific organs – selectable individually or in combination, always including blood analysis.',
      info: 'Whole-body MRI for comprehensive screening or specialized MRI scans for targeted screening of specific organs – selectable individually or in combination, always including blood analysis.',
      gender: 'Your biological sex',
      male: 'Male',
      female: 'Female',
    },
    from: 'from',
    mostPopular: 'Most popular',
    groups: {
      fullBody: {
        name: 'Whole Body',
        description:
          'Early detection of 500+ diseases, including cancer, neurological conditions, aneurysms, and spinal changes.',
        body1: 'Classic version with:',
        body2: 'Pro version additionally with:',
        price: '1,250 €',
        scanTime: 'approx. 60 min.',
      },
      bodyCompositionScan: {
        name: 'Body Composition',
        description: 'The gold standard for precise measurement of your body composition.',
        price: '249 €',
        scanTime: 'approx. 15 min.',
      },
      fullBodyClassic: {
        name: 'Whole Body Classic',
        description:
          'The solid foundation for your health screening – incl. whole-body MRI and blood analysis.',
      },
      fullBodyPro: {
        name: 'Whole Body Pro',
        description:
          'Our most comprehensive screening – additionally with heart MRI, prostate MRI (for men), and body composition scan.',
      },
      individual: {
        name: 'Individual',
        description: 'Customize your scan according to your needs.',
        body1: 'Selectable individually or together:',
        body2: 'Always included',
        price: '549 €',
        scanTime: 'approx. 30 min.',
      },
    },
    products: {
      wholeBodyMRI: {
        name: 'Whole Body MRI',
        description: 'Detailed scan of your entire body',
        subtitleFullBody:
          'for high-resolution images of brain, head, neck, spine, abdomen, pelvis, legs',
        subtitleIndividual:
          'for high-resolution images of brain, head, neck, spine, abdomen, pelvis, legs',
      },
      brainMRI: {
        name: 'Brain MRI',
        description: 'Detailed scan of your brain and blood vessels',
        subtitleIndividual:
          'for high-resolution images of brain (already included if Whole Body MRI is selected)',
      },
      heartMRI: {
        name: 'Heart MRI',
        description: 'Assessment of heart function and structure',
        subtitleFullBody:
          'for high-resolution images of the heart despite heartbeat (not sufficiently precise in whole-body MRI)',
        subtitleIndividual:
          'for high-resolution images of the heart despite heartbeat (not sufficiently precise in whole-body MRI)',
      },
      prostateMRI: {
        name: 'Prostate MRI',
        description: 'Specialized screening for the prostate gland',
        subtitleFullBody:
          'for high-resolution images of the prostate (not sufficiently precise in whole-body MRI)',
        subtitleIndividual:
          'for high-resolution images of the prostate (not sufficiently precise in whole-body MRI)',
      },
      bodyCompositionScan: {
        name: 'Body Composition Scan',
        description: 'Analysis of fat and muscle distribution',
        subtitleFullBody:
          'Gold standard for analysis of muscle volume, quality and fat distribution (abdomen, liver, subcutaneous)',
        subtitleIndividual:
          'Gold standard for analysis of muscle volume, quality and fat distribution (abdomen, liver, subcutaneous)',
        subtitleStandalone1:
          'Measurement of muscle volume and quality as well as fat volume and distribution (abdomen, liver, subcutaneous)',
        subtitleStandalone2:
          'Especially for individuals who want to actively improve their body or objectively track changes through nutrition, medication or training.',
        subtitleStandalone3: 'Good to know:',
        subtitleStandalone4:
          'the Body Composition MRI can also be performed as part of our comprehensive "Whole Body Scan Pro" or "Individual Scan".',
      },
      bloodAnalysis: {
        name: 'Blood Analysis',
        description: 'Comprehensive blood test for key health markers',
        subtitleIndividual:
          'Ideal complement to MRI – detects early changes that are not (yet) visible on MRI',
      },
    },
    addOns: {
      heading: 'Examinations',
      subtitle: 'Customize your scan individually to your health priorities.',
      classic: {
        name: 'Whole Body Classic',
        heading: 'Classic version with:',
      },
      pro: {
        name: 'Whole Body Pro',
        heading: 'Pro version additionally with:',
      },
      invalidCombination: 'This combination is unfortunately not possible.',
    },
    location: {
      heading: 'Where would you like to go?',
      subtitle: 'Choose one of our premium partner locations.',
      searchPlaceholder: 'Search locations...',
      viewOnMap: 'View on Google Maps',
    },
    appointment: {
      heading: 'When would you like to come?',
      subtitle: 'Select a convenient date and time for your appointment.',
      yourAppointment: 'Your Appointment:',
      selectedDate: 'Selected Date',
      noSlotSelected: 'Please select a time slot',
    },
    personalData: {
      heading: 'Your Details',
      subtitle: 'We need some information to process your booking.',
      infoTitle: 'Your Health is our Priority',
      infoSubtitle: 'Your data is securely stored and only shared with the medical staff.',
    },
    summary: {
      heading: 'Review your booking',
      subtitle: 'Please check your details before confirming the appointment.',
      nextStepsTitle: 'What happens next?',
      total: 'Total',
      step1: 'You will receive a confirmation email with all details.',
      step2: 'Fill out the digital medical questionnaire before your appointment.',
      step3: 'Arrive 15 minutes early to your appointment.',
      nextStep1Title: 'Confirm appointment',
      nextStep1Desc: 'Review your selection and complete the booking.',
      nextStep2Title: 'Pay on site',
      nextStep2Desc: 'Payment is made conveniently on the day of your examination at the practice.',
    },
  },
};
